import { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Link,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Alert
} from '@mui/material';
import FormErrorWrapper from '../common/FormErrorWrapper';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate, Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

function Login() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const phoneFromQuery = searchParams.get('phone');
  const showLegacyMessage = searchParams.get('legacy') === '1';

  const [activeTab, setActiveTab] = useState(location.pathname === '/register' ? 1 : 0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState(phoneFromQuery || '');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [address1, setAddress1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState({});
  const [birthdayError, setBirthdayError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptCinchCredentials, setAcceptCinchCredentials] = useState(false);
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneCodeMessage, setPhoneCodeMessage] = useState('');

  const navigate = useNavigate();
  const { login, register } = useAuth();

  useEffect(() => {
    setActiveTab(location.pathname === '/register' ? 1 : 0);
  }, [location.pathname]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setError({});
    const path = newValue === 0 ? '/login' : '/register';
    const queryString = phoneFromQuery ? `?phone=${encodeURIComponent(phoneFromQuery)}` : '';
    navigate(`${path}${queryString}`);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError({});

    try {
      setIsLoading(true);
      await login(email, password);
      navigate('/memberships');
    } catch (err) {
      setError({ non_field_errors: 'Invalid credentials. Check your inputs and try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const getDaysInMonth = (month, year) => {
    if (month === '2') {
      if (year) {
        const isLeapYear = (parseInt(year) % 4 === 0 && parseInt(year) % 100 !== 0) || parseInt(year) % 400 === 0;
        return isLeapYear ? 29 : 28;
      }
      return 29;
    }
    if (['1', '3', '5', '7', '8', '10', '12'].includes(month)) {
      return 31;
    }
    if (['4', '6', '9', '11'].includes(month)) {
      return 30;
    }
    return 31;
  };

  const validateBirthday = () => {
    if (!birthMonth && !birthDay) {
      setBirthdayError('');
      return true;
    }
    if ((birthMonth && !birthDay) || (!birthMonth && birthDay)) {
      setBirthdayError('Both month and day must be selected together');
      return false;
    }
    const daysInMonth = getDaysInMonth(birthMonth, birthYear);
    if (parseInt(birthDay) > daysInMonth || parseInt(birthDay) < 1) {
      setBirthdayError(`Invalid day for selected month. Day should be between 1 and ${daysInMonth}`);
      return false;
    }
    setBirthdayError('');
    return true;
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError({});
    setBirthdayError('');

    if (!validateBirthday()) {
      return;
    }

    try {
      setIsLoading(true);
      const result = await register({
        firstName,
        lastName,
        email,
        phone,
        password,
        confirmPassword,
        address1,
        city,
        state,
        zipCode,
        birthMonth,
        birthDay,
        birthYear,
        phoneCode: phoneCodeMessage ? phoneCode : undefined
      });
      if (result?.phoneVerificationRequired) {
        setPhoneCodeMessage(result.detail || 'Enter the code we texted to this phone number to finish registering.');
        return;
      }
      navigate('/memberships');
    } catch (err) {
      setError(err?.response?.data || { non_field_errors: 'Registration failed. Please try again' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth={activeTab === 1 ? "md" : "xs"}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: activeTab === 1 ? 2 : 4, sm: 4 }, 
          mt: { xs: activeTab === 1 ? 2 : 8, sm: 8 }, 
          mb: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          boxShadow: { xs: activeTab === 1 ? 'none' : 3, sm: 3 },
          border: { xs: activeTab === 1 ? 'none' : '1px solid', sm: '1px solid' },
          borderColor: { xs: 'transparent', sm: 'divider' }
        }}
      >
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="auth tabs" sx={{ mb: 3, width: '100%' }}>
          <Tab
            label="Login"
            icon={<LockOutlinedIcon />}
            iconPosition="start"
            sx={{ flexGrow: 1 }}
          />
          <Tab
            label="Register"
            icon={<PersonAddIcon />}
            iconPosition="start"
            sx={{ flexGrow: 1 }}
          />
        </Tabs>

        {error && error.non_field_errors && (
          <Typography color="error" sx={{ mt: 2, mb: 2 }}>
            {error.non_field_errors}
          </Typography>
        )}

        {/* Login Form */}
        {activeTab === 0 && (
          <>
            {showLegacyMessage && (
              <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
                We've updated our portal. If this is the first time you've tried to log in in awhile you may need to reset your password
              </Alert>
            )}
            <Box component="form" onSubmit={handleLoginSubmit} sx={{ mt: 1, width: '100%' }}>
              <FormErrorWrapper error={error.email}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </FormErrorWrapper>
              <FormErrorWrapper error={error.password}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </FormErrorWrapper>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>

              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Link
                  href="https://marketing.cinch.io"
                  rel="noopener noreferrer"
                  variant="body2"
                >
                  Business User Login
                </Link>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 1 }}>
                <Link component={RouterLink} to="/reset-password" variant="body2">
                  Forgot password?
                </Link>
              </Box>
            </Box>
          </>
        )}

        {/* Registration Form */}
        {activeTab === 1 && (
          <>
            <Box component="form" onSubmit={handleRegisterSubmit} sx={{ mt: 1, width: '100%' }}>
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormErrorWrapper error={error.first_name}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="firstName"
                      label="First Name"
                      name="firstName"
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={isLoading}
                    />
                  </FormErrorWrapper>
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormErrorWrapper error={error.last_name}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="lastName"
                      label="Last Name"
                      name="lastName"
                      autoComplete="family-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={isLoading}
                    />
                  </FormErrorWrapper>
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormErrorWrapper error={error.email}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="registerEmail"
                      label="Email Address"
                      name="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                    />
                  </FormErrorWrapper>
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormErrorWrapper error={error.phone}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="phone"
                      label="Phone Number"
                      name="phone"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={isLoading || !!phoneFromQuery}
                    />
                  </FormErrorWrapper>
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormErrorWrapper error={error.password}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="registerPassword"
                      label="Password"
                      type={showRegisterPassword ? 'text' : 'password'}
                      id="registerPassword"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                                onMouseDown={(e) => e.preventDefault()}
                                edge="end"
                              >
                                {showRegisterPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </FormErrorWrapper>
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <FormErrorWrapper error={error.confirm_password}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="confirmPassword"
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isLoading}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle confirm password visibility"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                onMouseDown={(e) => e.preventDefault()}
                                edge="end"
                              >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </FormErrorWrapper>
                </Grid>
                <Grid item size={12}>
                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                    Address Information (Optional)
                  </Typography>
                </Grid>
                <Grid item size={12}>
                  <FormErrorWrapper error={error.address1}>
                    <TextField
                      margin="normal"
                      fullWidth
                      id="address1"
                      label="Address"
                      name="address1"
                      autoComplete="street-address"
                      value={address1}
                      onChange={(e) => setAddress1(e.target.value)}
                      disabled={isLoading}
                    />
                  </FormErrorWrapper>
                </Grid>
                <Grid item size={4}>
                  <FormErrorWrapper error={error.city}>
                    <TextField
                      margin="normal"
                      fullWidth
                      id="city"
                      label="City"
                      name="city"
                      autoComplete="address-level2"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      disabled={isLoading}
                    />
                  </FormErrorWrapper>
                </Grid>
                <Grid item size={4}>
                  <FormErrorWrapper error={error.state}>
                    <TextField
                      margin="normal"
                      fullWidth
                      id="state"
                      label="State"
                      name="state"
                      autoComplete="address-level1"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      disabled={isLoading}
                    />
                  </FormErrorWrapper>
                </Grid>
                <Grid item size={4}>
                  <FormErrorWrapper error={error.zip_code}>
                    <TextField
                      margin="normal"
                      fullWidth
                      id="zipCode"
                      label="ZIP Code"
                      name="zipCode"
                      autoComplete="postal-code"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                      disabled={isLoading}
                    />
                  </FormErrorWrapper>
                </Grid>
                <Grid item size={12}>
                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                    Birthday Information (Optional)
                  </Typography>
                </Grid>
                <Grid item size={4}>
                  <FormErrorWrapper error={error.birth_month}>
                    <TextField
                      select
                      margin="normal"
                      fullWidth
                      label="Month"
                      name="birthMonth"
                      value={birthMonth}
                      onChange={(e) => setBirthMonth(e.target.value)}
                      disabled={isLoading}
                      slotProps={{
                        select: {
                          native: true,
                        },
                      }}
                    >
                      <option value=""></option>
                      <option value="1">January</option>
                      <option value="2">February</option>
                      <option value="3">March</option>
                      <option value="4">April</option>
                      <option value="5">May</option>
                      <option value="6">June</option>
                      <option value="7">July</option>
                      <option value="8">August</option>
                      <option value="9">September</option>
                      <option value="10">October</option>
                      <option value="11">November</option>
                      <option value="12">December</option>
                    </TextField>
                  </FormErrorWrapper>
                </Grid>
                <Grid item size={4}>
                  <FormErrorWrapper error={error.birth_day}>
                    <TextField
                      select
                      margin="normal"
                      fullWidth
                      label="Day"
                      name="birthDay"
                      value={birthDay}
                      onChange={(e) => setBirthDay(e.target.value)}
                      disabled={isLoading}
                      slotProps={{
                        select: {
                          native: true,
                        },
                      }}
                    >
                      <option value=""></option>
                      {[...Array(31)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}
                        </option>
                      ))}
                    </TextField>
                  </FormErrorWrapper>
                </Grid>
                <Grid item size={4}>
                  <FormErrorWrapper error={error.birth_year}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label="Year"
                      name="birthYear"
                      value={birthYear}
                      onChange={(e) => setBirthYear(e.target.value)}
                      disabled={isLoading}
                      slotProps={{
                        htmlInput: {
                          maxLength: 4,
                          pattern: '[0-9]*'
                        }
                      }}
                      placeholder="YYYY"
                    />
                  </FormErrorWrapper>
                </Grid>
                {birthdayError && (
                  <Grid item size={12}>
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                      {birthdayError}
                    </Typography>
                  </Grid>
                )}
                {phoneCodeMessage && (
                  <Grid item size={12}>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      This phone number is already enrolled at a store. {phoneCodeMessage}
                    </Alert>
                    <FormErrorWrapper error={error.phone_code}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="phoneCode"
                        label="Verification Code"
                        name="phoneCode"
                        autoComplete="one-time-code"
                        value={phoneCode}
                        onChange={(e) => setPhoneCode(e.target.value)}
                        disabled={isLoading}
                        slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6 } }}
                      />
                    </FormErrorWrapper>
                  </Grid>
                )}
                <Grid item size={12}>
                  <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    name="acceptTerms"
                    color="primary"
                    disabled={isLoading}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ mt: '10px' }}>
                    I agree to the{' '}
                    <Link
                      href="https://portal.cinch.io/terms/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms and Services
                    </Link>
                  </Typography>
                }
                sx={{ mt: 2, alignItems: 'flex-start' }}
                  />
                </Grid>
                <Grid item size={12}>
                  <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptCinchCredentials}
                    onChange={(e) => setAcceptCinchCredentials(e.target.checked)}
                    name="acceptCinchCredentials"
                    color="primary"
                    disabled={isLoading}
                    sx={{ mt: '2px' }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ mt: '10px' }}>
                    By registering, you're creating secure Cinch credentials. These work like "Sign in with Google/Apple"—you can use the same login to access loyalty rewards and other experiences from any business powered by Cinch.
                  </Typography>
                }
                sx={{ mt: 1, alignItems: 'flex-start' }}
                  />
                </Grid>
                <Grid item size={12}>
                  <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading || !acceptTerms || !acceptCinchCredentials}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Register'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
}

export default Login;